import backdropMain from '@/assets/backdrop-main.png';
import { UnifiedHeader } from '@/components/UnifiedHeader';
import { useOrganizationDepartments } from '@/organization/useOrganizations';
import type { Department } from '@/types/department';
import { apiFetch } from '@/util/apiClient';
import { getErrorMessage } from '@/util/errorUtils';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

// =============================================================================
// RESPONSIVE SIZING SYSTEM (grabbed from Login.tsx)
// =============================================================================
// Figma specs (max size): Container 552x409, inputs 476x52, button 476x38
// All values scale proportionally using CSS clamp() with container width as base
// Container uses 25vw as preferred value (reduced from 29vw for better screen proportions)
// Min values = max * 0.667 (scales down to ~318px width at smaller viewports)
// =============================================================================

// Container dimensions - 552:409 aspect ratio (1.35:1)
const CONTAINER_WIDTH = 'clamp(448px, 27vw, 409px)';

// Horizontal padding: (552 - 476) / 2 = 38px each side
const PADDING_X = 'clamp(21px, calc(25vw * 0.069), 33px)'; // 38/552 = 6.9%
// Vertical padding
const PADDING_TOP = 'clamp(17px, calc(25vw * 0.054), 26px)'; // reduced from 40px
const PADDING_BOTTOM = 'clamp(14px, calc(25vw * 0.045), 22px)'; // small padding at bottom

// Font sizes
const TITLE_FONT_SIZE = 'clamp(21px, calc(25vw * 0.065), 31px)'; // 36px in Figma
const ERROR_FONT_SIZE = 'clamp(8px, calc(25vw * 0.025), 12px)'; // Increased min from 8px to 10px
const BUTTON_FONT_SIZE = 'clamp(10px, calc(25vw * 0.032), 15px)'; // Increased min from 10px to 14px

// Element heights from Figma
const ERROR_HEIGHT = 'clamp(29px, calc(25vw * 0.091), 44px)'; // restored to 50px
const INPUT_HEIGHT = 'clamp(30px, calc(25vw * 0.094), 45px)'; // 52px in Figma
const BUTTON_HEIGHT = 'clamp(22px, calc(25vw * 0.069), 33px)'; // 38px in Figma

// Margins
const MARGIN_SM = 'clamp(4px, calc(25vw * 0.013), 6px)'; // ~7px (tiny bit more after password)
const MARGIN_MD = 'clamp(10px, calc(25vw * 0.029), 14px)'; // Increased min from 7px to 10px

interface InvitationDetails {
  email: string;
  role: string;
  organizationName: string;
  organizationId: string;
}

export default function SignupPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [selectedDepartments, setDepartments] = useState([] as Department[]);
  const [contactOpen, setContactOpen] = useState(false);

  const {
    data: invitationDetails,
    isLoading: isLoadingInvitation,
    error: invitationError,
  } = useQuery({
    queryKey: ['invitation', code],
    queryFn: async (): Promise<InvitationDetails> => {
      const response = await apiFetch(`/auth/invite/${code}/`);
      return response as InvitationDetails;
    },
    enabled: !!code,
    retry: false,
  });
  const { data: departments } = useOrganizationDepartments(
    invitationDetails?.organizationId,
  );

  // Pre-populate email when invitation details are loaded
  useEffect(() => {
    if (invitationDetails?.email) {
      setEmail(invitationDetails.email);
    }
  }, [invitationDetails]);
  const { mutate: doSignup, status } = useMutation({
    mutationFn: async () => {
      await apiFetch(`/auth/invite/${code}/`, {
        method: 'POST',
        body: JSON.stringify({
          email,
          firstName: firstName,
          lastName: lastName,
          role: invitationDetails?.role,
          password,
          phone: phone,
          departmentIds: selectedDepartments.map((d) => d.id),
        }),
      });
    },
    onSuccess: () => {
      navigate('/login');
    },
    onError: (err: unknown) => {
      setError(getErrorMessage(err));
    },
  });

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#003D36',
      borderRadius: 0,
      fontFamily: '"Inter", sans-serif',
      '& fieldset': {
        border: 'none',
        borderBottom: '2px solid #7BBCB3',
      },
      '&:hover fieldset': {
        borderBottom: '2px solid #7BBCB3',
      },
      '&.Mui-focused fieldset': {
        borderBottom: '2px solid #7BBCB3',
      },
    },
    '& .MuiOutlinedInput-input': {
      color: '#ffffff',
      fontFamily: '"Inter", sans-serif',
      '&::placeholder': {
        color: '#7BBCB3',
        opacity: 1,
        fontWeight: 600,
      },
    },
    '& .MuiInputLabel-root': {
      color: '#7BBCB3',
      fontFamily: '"Inter", sans-serif',
      '&.Mui-focused': {
        color: '#7BBCB3',
      },
    },
  };

  if (isLoadingInvitation) {
    return (
      <Container maxWidth={'xs'}>
        <Paper
          elevation={6}
          sx={{
            p: 4,
            mt: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <CircularProgress />
          <Typography>Validating invitation...</Typography>
        </Paper>
      </Container>
    );
  }

  if (invitationError) {
    return (
      <Container maxWidth={'xs'}>
        <Paper
          elevation={6}
          sx={{ p: 4, mt: 8, display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <Typography variant={'h4'} gutterBottom>
            Invalid Invitation
          </Typography>
          <Alert severity={'error'}>
            {invitationError instanceof Error
              ? invitationError.message
              : 'This invitation code is invalid or has already been used.'}
          </Alert>
          <Stack direction='row' spacing={2} justifyContent='center'>
            <Button
              variant='outlined'
              component={Link}
              to='/login'
              sx={{ textDecoration: 'none' }}
            >
              Go to Login
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth={'xs'}>
      {/* Fixed background image */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${backdropMain})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: -1,
        }}
      />
      <UnifiedHeader
        hideProfileButton
        contactOpen={contactOpen}
        onContactToggle={() => setContactOpen(!contactOpen)}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          overflowY: 'scroll',
        }}
      >
        <Box
          sx={{
            width: CONTAINER_WIDTH,
            pointerEvents: 'auto',
          }}
        >
          <Box
            sx={{
              backgroundColor: 'rgba(14, 70, 63, 1)',
              paddingLeft: PADDING_X,
              paddingRight: PADDING_X,
              paddingTop: PADDING_TOP,
              paddingBottom: PADDING_BOTTOM,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Typography
              sx={{
                color: '#ffffff',
                fontFamily: '"Futura PT", "Futura", sans-serif',
                fontWeight: 700,
                fontSize: TITLE_FONT_SIZE,
                marginBottom: MARGIN_SM,
              }}
            >
              Sign Up
            </Typography>

            <Box
              sx={{
                height: ERROR_HEIGHT,
                display: 'flex',
                alignItems: 'flex-start',
                mb: MARGIN_SM,
              }}
            >
              {error && (
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: ERROR_FONT_SIZE,
                    fontWeight: 600,
                    color: 'rgba(230, 174, 174, 1)',
                  }}
                >
                  *The following error occurred: {error}. Please{' '}
                  <Box
                    component='span'
                    onClick={() => setContactOpen(true)}
                    sx={{
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                  >
                    contact CSI if you are having issues
                  </Box>
                  .
                </Typography>
              )}
            </Box>
            <Box
              component={'form'}
              onSubmit={(e) => {
                e.preventDefault();
                setError('');
                doSignup();
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                flex: 1,
              }}
            >
              {invitationDetails && (
                <Alert
                  severity={'info'}
                  sx={{
                    backgroundColor: '#26675F',
                    color: '#ffffff',
                    marginBottom: MARGIN_MD,
                    fontFamily: '"Inter", sans-serif',
                    fontSize: BUTTON_FONT_SIZE,
                  }}
                >
                  You're being invited to join{' '}
                  <strong>{invitationDetails.organizationName}</strong> as a(n):{' '}
                  <strong>{invitationDetails.role}</strong>
                </Alert>
              )}
              <TextField
                fullWidth
                label={'First Name'}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                sx={{
                  ...inputStyles,
                  marginBottom: MARGIN_MD,
                  '& .MuiOutlinedInput-root': {
                    ...inputStyles['& .MuiOutlinedInput-root'],
                    height: INPUT_HEIGHT,
                    minHeight: INPUT_HEIGHT,
                    boxSizing: 'border-box',
                  },
                  '& .MuiOutlinedInput-input': {
                    ...inputStyles['& .MuiOutlinedInput-input'],
                    height: '100%',
                    boxSizing: 'border-box',
                    padding: '0 14px',
                  },
                }}
              />
              <TextField
                fullWidth
                label={'Last Name'}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                sx={{
                  ...inputStyles,
                  marginBottom: MARGIN_MD,
                  '& .MuiOutlinedInput-root': {
                    ...inputStyles['& .MuiOutlinedInput-root'],
                    height: INPUT_HEIGHT,
                    minHeight: INPUT_HEIGHT,
                    boxSizing: 'border-box',
                  },
                  '& .MuiOutlinedInput-input': {
                    ...inputStyles['& .MuiOutlinedInput-input'],
                    height: '100%',
                    boxSizing: 'border-box',
                    padding: '0 14px',
                  },
                }}
              />
              <Autocomplete
                multiple
                size='small'
                options={departments || []}
                getOptionLabel={(option) => option.name}
                value={selectedDepartments}
                onChange={(_event, newValue) => setDepartments(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Departments'
                    sx={{ fontFamily: '"Inter", sans-serif' }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        size='small'
                        label={option.name}
                        {...tagProps}
                        sx={{ fontFamily: '"Inter", sans-serif' }}
                      />
                    );
                  })
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                sx={{
                  ...inputStyles,
                  marginBottom: MARGIN_MD,
                  '& .MuiOutlinedInput-root': {
                    ...inputStyles['& .MuiOutlinedInput-root'],
                    height: INPUT_HEIGHT,
                    minHeight: INPUT_HEIGHT,
                    boxSizing: 'border-box',
                  },
                  '& .MuiOutlinedInput-input': {
                    ...inputStyles['& .MuiOutlinedInput-input'],
                    height: '100%',
                    boxSizing: 'border-box',
                    padding: '0 14px',
                  },
                }}
              />
              <TextField
                fullWidth
                label={'Email'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                type={'email'}
                sx={{
                  ...inputStyles,
                  marginBottom: MARGIN_MD,
                  '& .MuiOutlinedInput-root': {
                    ...inputStyles['& .MuiOutlinedInput-root'],
                    height: INPUT_HEIGHT,
                    minHeight: INPUT_HEIGHT,
                    boxSizing: 'border-box',
                  },
                  '& .MuiOutlinedInput-input': {
                    ...inputStyles['& .MuiOutlinedInput-input'],
                    height: '100%',
                    boxSizing: 'border-box',
                    padding: '0 14px',
                  },
                }}
              />
              <TextField
                fullWidth
                label={'Phone Number'}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                sx={{
                  ...inputStyles,
                  marginBottom: MARGIN_MD,
                  '& .MuiOutlinedInput-root': {
                    ...inputStyles['& .MuiOutlinedInput-root'],
                    height: INPUT_HEIGHT,
                    minHeight: INPUT_HEIGHT,
                    boxSizing: 'border-box',
                  },
                  '& .MuiOutlinedInput-input': {
                    ...inputStyles['& .MuiOutlinedInput-input'],
                    height: '100%',
                    boxSizing: 'border-box',
                    padding: '0 14px',
                  },
                }}
              />
              <TextField
                fullWidth
                label={'Password'}
                type={'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                sx={{
                  ...inputStyles,
                  marginBottom: MARGIN_MD,
                  '& .MuiOutlinedInput-root': {
                    ...inputStyles['& .MuiOutlinedInput-root'],
                    height: INPUT_HEIGHT,
                    minHeight: INPUT_HEIGHT,
                    boxSizing: 'border-box',
                  },
                  '& .MuiOutlinedInput-input': {
                    ...inputStyles['& .MuiOutlinedInput-input'],
                    height: '100%',
                    boxSizing: 'border-box',
                    padding: '0 14px',
                  },
                }}
              />
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: ERROR_FONT_SIZE,
                  fontWeight: 600,
                  color: '#7BBCB3',
                  marginBottom: MARGIN_SM,
                }}
              >
                Password must have at least 8 characters and include at least
                one letter. Please do not use common words or a password similar
                to your username, email, or name.
                {/* NOTE: need to check if password validation actually works in production */}
              </Typography>

              <Box sx={{ marginTop: 'auto' }}>
                <Button
                  fullWidth
                  variant='contained'
                  type='submit'
                  disabled={status === 'pending'}
                  disableRipple
                  sx={{
                    backgroundColor: 'rgba(9, 126, 113, 1) !important',
                    color: '#ffffff !important',
                    fontWeight: 600,
                    fontSize: BUTTON_FONT_SIZE,
                    height: BUTTON_HEIGHT,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: 'none',
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                      backgroundColor: '#0a9485 !important',
                      boxShadow: 'none',
                    },
                    '&:active': {
                      backgroundColor: '#065a52 !important',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#4a7d78 !important',
                      color: 'rgba(255, 255, 255, 0.4) !important',
                    },
                  }}
                >
                  {status === 'pending'
                    ? 'Creating account...'
                    : 'Create Account'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ height: '20px' }}></Box>
    </Container>
  );
}
